// Загрузка GLB через @loaders.gl/gltf и упаковка в WebGL2-буферы для рендера.
// Поддерживается: multi-mesh (все primitives, не только первый), skinning
// (JOINTS_0/WEIGHTS_0 + inverseBindMatrices), node tree (parent/local TRS),
// animations с whitelist имён, predcomputed upper-body mask.
//
// loaders.gl даёт нам resolved accessors (TypedArray), но иерархия и животные
// каналы остаются в "raw" виде — мы ходим по `gltf.nodes`/`gltf.animations`
// сами и преобразуем в формат, удобный для AnimationMixer'а.

import { load } from "@loaders.gl/core";
import { GLTFLoader, postProcessGLTF } from "@loaders.gl/gltf";

import { refineInnerSkinMeshForBodyVox } from "./bodyVoxMeshRefine";

/** Маппинг "оригинальное имя анимации в GLB" → "семантическое имя в движке".
 *  Всё остальное (Layer0, .001 и пр.) игнорируется.
 *
 *  В новом soldier_animated.glb (Mixamo-набор без пальцев) клипы названы:
 *    rifleIdle / rifleRun / firingRifle / FallingBackDeath — база (idle/run/shoot/die),
 *    idle crouching / crouching walk — присед (crouchIdle / crouchWalk),
 *    jump / jumpDown — прыжок и приземление.
 *  Старые альтернативные имена (idle/SlowRun/RunForward/Gunplay/dying) тоже
 *  поддерживаем для совместимости со старыми мешами. parseAnimations берёт
 *  ПЕРВУЮ найденную анимацию для каждого semantic-имени, дубликаты пропускает. */
const ANIM_NAME_MAP: Record<string, string> = {
  rifleIdle: "idle",
  rifleRun: "run",
  firingRifle: "shoot",
  // Смерти: "die" — падение на спину (выстрел в ПЕРЕДНЮЮ часть тела),
  //         "dieBack" — падение лицом вперёд (выстрел в СПИНУ).
  // Используются в логике NPC (см. client/src/engine/npc.ts).
  FallingBackDeath: "die",
  deathFromTheBack: "dieBack",
  // Hurt-flinch: короткая реакция на попадание (корпус дёргается).
  // One-shot, играется как base-анимация; при повторных хитах рестартится
  // (перезапуск baseTime=0), после окончания возвращаем в idle.
  hurting: "hurt",
  // Присед и прыжок (имена клипов совпадают с нашими semantic-именами
  // кроме пробелов — делаем явные маппинги).
  "idle crouching": "crouchIdle",
  "crouching walk": "crouchWalk",
  jump: "jump",
  jumpDown: "jumpDown",
  // Тормозная анимация при резкой остановке с бега. В GLB может быть
  // дубликат `runToStop.001` (второй шаг с другой ноги) — первый
  // найденный займёт semantic-имя, остальные игнорируются.
  runToStop: "runToStop",
  // Направленный бег: чистые стрейфы (A, D без W/S) и диагонали вперёд
  // (W+A, W+D). См. state machine в main.ts для логики выбора.
  // Несколько алиасов под разные стили названий из Blender/Mixamo.
  runLeft: "runLeft",
  runRight: "runRight",
  runForwardLeft: "runForwardLeft",
  runForwardRight: "runForwardRight",
  "Run left": "runLeft",
  "Run Left": "runLeft",
  "Run right": "runRight",
  "Run Right": "runRight",
  "Run Forward Left": "runForwardLeft",
  "Run forward Left": "runForwardLeft",
  "Run Forward Right": "runForwardRight",
  "Run forward Right": "runForwardRight",
  // Уровень alone_in_the_dark: анимация двери особняка (one-shot, не idle).
  door_manison_open: "door_manison_open",
  door_mansion_open: "door_manison_open",
  // Межкомнатные двери level-pack'а: общие клипы полотен (Blender actions
  // DoorOpen/DoorClose шарятся всеми одиночными дверями — в glTF один клип
  // с каналами всех полотен; при проигрывании фильтруем каналы по нодам
  // конкретной двери, см. renderer doorLayers).
  DoorOpen: "DoorOpen",
  DoorClose: "DoorClose",
  DblOpenL: "DblOpenL",
  DblOpenR: "DblOpenR",
  DblCloseL: "DblCloseL",
  DblCloseR: "DblCloseR",
  // legacy mappings
  SlowRun: "walk",
  RunForward: "run",
  Gunplay: "shoot",
  dying: "die",
  // Пропсы (ammo/medkit): клип вращения из Blender.
  rotation: "idle",
  Rotation: "idle",
};

/** Имя cutoff-кости для upper-body mask. Всё что в subtree этой ноды
 *  (включительно) → попадает в overlay-слой при наложении стрельбы поверх
 *  ходьбы/бега. Низ (Hips + Spine + ноги) остаётся от base-слоя. */
// Spine, а не Spine1 — потому что `weapone_bone` (и оружие) приparent'ены
// к Spine через Bone-Parent в Blender. Чтобы отдача firingRifle дошла до
// оружия, надо чтобы Spine тоже принимал firing-позу; иначе Spine остаётся
// в base-анимации (idle/run), и его subtree (включая weapone_bone) не
// двигается под стрельбу.
const UPPER_BODY_CUTOFF = "mixamorig:Spine";

/** Клипы, которые должны проигрываться "на месте": translation у
 *  mixamorig:Hips полностью фиксируется на значении первого ключа. Нужно
 *  для Mixamo-клипов, скачанных БЕЗ галочки In Place (root motion запечён
 *  в Hips). Важно: мы зануляем ВСЕ три компоненты, потому что в Mixamo
 *  post-Blender frame направление "forward" у Hips не совпадает с глобальным
 *  Y — оно идёт по локальной оси бона (у runToStop — это как раз Y канал). */
const IN_PLACE_CLIPS: string[] = ["runToStop"];

/** Триммирование клипа слева: убираем все ключи с time < cutoff, остальные
 *  сдвигаем на -cutoff. Используется чтобы вырезать "разгон" из runToStop
 *  и оставить только тормозной шаг. key/value = имя клипа / cutoff в сек.
 *  Итоговая длительность = original_duration - cutoff. */
const CLIP_TRIM_START: Record<string, number> = {
  // runToStop: пользователь в Blender оставил только финальный тормозной
  // шаг, начиная с frame 25 (30fps → t=0.833с). Мы режем всё до 25 кадра
  // и сдвигаем остаток к 0. Итог: клип ~0.67с — динамичная остановка
  // в темпе шутера. Если захотим ещё короче — уменьшаем cutoff.
  runToStop: 25 / 30,
};

/** Возвращает (x,y,z) — первый ключ translation-канала mixamorig:Hips
 *  в заданном клипе. null если канала нет. */
function getHipsFirstTranslation(
  clip: AnimationClip, nodes: NodeData[],
): [number, number, number] | null {
  for (const ch of clip.channels) {
    if (ch.path !== "translation") continue;
    const node = nodes[ch.targetNode];
    if (!node || node.name !== "mixamorig:Hips") continue;
    if (ch.values.length < 3) continue;
    return [ch.values[0], ch.values[1], ch.values[2]];
  }
  return null;
}

/** Фиксирует translation-канал mixamorig:Hips на заданной константе.
 *  Используется чтобы "перенести" клип на baseline соседнего анима
 *  (например runToStop → idle): после такой обработки при crossfade
 *  между клипами Hips НЕ прыгает, транзиция получается визуально
 *  бесшовной.
 *
 *  Зачем переносить, а не просто стрипить: у Mixamo-клипов каждый
 *  имеет свой уникальный baseline Hips (например idle Z=-98.7,
 *  rifleRun Z=-83, runToStop Z=-90.7). Если клип runToStop хотя бы
 *  по END-позе совпадает с idle, то переход runToStop → idle
 *  становится швом из "одной и той же позы в одну и ту же позу". */
function pinHipsToConstant(
  clip: AnimationClip, nodes: NodeData[], x: number, y: number, z: number,
): void {
  for (const ch of clip.channels) {
    if (ch.path !== "translation") continue;
    const node = nodes[ch.targetNode];
    if (!node || node.name !== "mixamorig:Hips") continue;
    const values = ch.values;
    for (let i = 0; i < values.length; i += 3) {
      values[i] = x;
      values[i + 1] = y;
      values[i + 2] = z;
    }
  }
}

/** Обрезать начало клипа: выбрасываем ключи с time < cutoff, остальные
 *  сдвигаем к 0. Длительность клипа пересчитывается. Если cutoff >=
 *  duration — клип вырождается в одиночную финальную позу. */
function trimClipStart(clip: AnimationClip, cutoff: number): void {
  if (cutoff <= 0) return;
  for (const ch of clip.channels) {
    const n = ch.times.length;
    if (n === 0) continue;
    let startIdx = 0;
    while (startIdx < n && ch.times[startIdx] < cutoff) startIdx++;
    // Хотя бы один ключ должен остаться (последний, если все время < cutoff).
    if (startIdx >= n) startIdx = n - 1;
    const newCount = n - startIdx;
    const newTimes = new Float32Array(newCount);
    const newValues = new Float32Array(newCount * ch.components);
    for (let i = 0; i < newCount; i++) {
      newTimes[i] = Math.max(0, ch.times[startIdx + i] - cutoff);
    }
    const srcValStart = startIdx * ch.components;
    for (let i = 0; i < newCount * ch.components; i++) {
      newValues[i] = ch.values[srcValStart + i];
    }
    ch.times = newTimes;
    ch.values = newValues;
  }
  // Пересчёт duration по максимальному time среди всех каналов.
  let maxT = 0;
  for (const ch of clip.channels) {
    if (ch.times.length === 0) continue;
    const last = ch.times[ch.times.length - 1];
    if (last > maxT) maxT = last;
  }
  clip.duration = maxT;
}

/** Имена костей, которые скрываем у локального игрока в FP-режиме (subtree
 *  каждой коллапсируется в 0). Голова, шея, и кости пальцев на правой руке
 *  (чтобы автомат не закрывал лицо игрока пальцами модели). */
export const FP_HIDDEN_BONE_NAMES = [
  "mixamorig:Head",
  "mixamorig:Neck",
];

/** Per-vertex attribute locations (должны совпадать с mesh.vert). */
export const ATTR_POS    = 0;
export const ATTR_NORMAL = 1;
export const ATTR_UV     = 2;
export const ATTR_JOINTS  = 3;
export const ATTR_WEIGHTS = 4;
/** glTF COLOR_0 — линейный множитель к baseColor (как в glTF 2.0). */
export const ATTR_COLOR = 5;

export interface SubMesh {
  vao: WebGLVertexArrayObject;
  indexCount: number;
  indexType: GLenum;
  diffuseTex: WebGLTexture | null;
  /** glTF `material.normalTexture`; линейное RGB, tangent-space в шейдере. */
  normalTex: WebGLTexture | null;
  /** glTF `normalTexture.scale` (по умолчанию 1). */
  normalScale: number;
  /** Если baseColor — HTMLVideoElement: обновлять texImage2D каждый кадр в renderer. */
  diffuseVideo: HTMLVideoElement | null;
  /** glTF `pbrMetallicRoughness.metallicRoughnessTexture` (linear, G=roughness B=metallic). */
  metallicRoughnessTex: WebGLTexture | null;
  metallicFactor: number;
  roughnessFactor: number;
  /** 0 = OPAQUE, 1 = MASK, 2 = BLEND (glTF alphaMode). */
  alphaMode: number;
  alphaCutoff: number;
  /** glTF `pbrMetallicRoughness.baseColorFactor` (линейный), шейдер умножает на tex и vColor. */
  baseColorFactor: [number, number, number, number];
  /** true если у primitive есть JOINTS_0/WEIGHTS_0 (использовать uBones). */
  hasSkin: boolean;
  /** Индекс node, который ссылается на этот mesh. Для node-attached
   *  объектов (например weapon) используется как mat = inst.model · nodeWorld[nodeIdx]. */
  nodeIdx: number;
  /** CPU-копия vertex данных для пересчёта bbox при заданной позе.
   *  Нужно потому что rest-bbox не равен render-bbox (Mixamo idle уже
   *  ставит ноги в Y=0 относительно mesh origin, а в bind-позе они на Y≈-0.94).
   *  Без этого пересчёта `soldierFloorOffset` поднимает солдата на ~0.9м над полом. */
  cpuVerts: {
    positions: Float32Array;
    joints: Uint16Array | null;   // null если hasSkin=false
    weights: Float32Array | null; // null если hasSkin=false
  };
  /** Локальный AABB примитива (node-space, из POSITION). Для frustum-куллинга
   *  сабмешей static-меша уровня: мировой AABB = (model · nodeWorld) × этот bbox. */
  bboxMin: [number, number, number];
  bboxMax: [number, number, number];
  /** GLB `material.extras.TIPADUMA_BODY_LAYER`: outer / inner / voxel (voxel → inner при загрузке). */
  bodyLayer: "outer" | "inner" | null;
  /** glTF `material.doubleSided` + эвристика тонких дверей (back-face cull). */
  doubleSided: boolean;
  /** `ghost_*` из extras ноды: `character` — billboarding к локальной камере (клиент); иначе плоскость в мире. */
  ghostKind: "character" | "picture" | null;
  /** Радианы; extras `ghost_billboard_yaw_deg` — поправка оси плоскости после billboarding. */
  ghostBillboardYawOffsetRad: number;
}

export interface SkinData {
  /** node indices в порядке для uBones (skin.joints). */
  joints: number[];
  /** Float32Array длиной 16 * jointCount. */
  inverseBindMatrices: Float32Array;
}

export interface NodeData {
  name: string;
  parent: number;       // -1 если корень
  children: number[];
  /** Локальная rest-pose TRS (для node'ов без animation channel). */
  translation: [number, number, number];
  rotation: [number, number, number, number]; // quaternion (xyzw)
  scale: [number, number, number];
  /** index в gltf.meshes или -1. */
  meshIdx: number;
}

export type AnimationPath = "translation" | "rotation" | "scale";

export interface AnimationChannel {
  targetNode: number;
  path: AnimationPath;
  /** Времена ключей в секундах (отсортированы по возрастанию). */
  times: Float32Array;
  /** Значения: для translation/scale 3 числа на ключ, для rotation 4 (xyzw). */
  values: Float32Array;
  /** Для отрисовки knotов нужен размер: 3 или 4. */
  components: 3 | 4;
}

export interface AnimationClip {
  /** Семантическое имя (idle/walk/run/shoot/die). */
  name: string;
  /** Длительность = max(channel.times[end]). */
  duration: number;
  channels: AnimationChannel[];
}

export interface LoadedMesh {
  subMeshes: SubMesh[];
  skin: SkinData | null;
  nodes: NodeData[];
  /** Топологический порядок обхода нод: родитель ВСЕГДА встречается раньше
   *  своих потомков. Нужен для FK (`computeNodeWorldMatrices`), потому что
   *  glTF не гарантирует, что `nodes[]` уже отсортирован — Blender,
   *  например, кладёт корневой Armature в самый конец. */
  nodeTraversalOrder: number[];
  /** Карта семантическое имя → AnimationClip. */
  animations: Map<string, AnimationClip>;
  /** Маска per-joint (длина = skin.joints.length).
   *  mask[j] = 1 если joint в upper-body subtree (override), 0 иначе.
   *  null если у меша нет skin'а. */
  upperBodyMask: Uint8Array | null;
  /** Доп. ноды (НЕ skin-joints), которые тоже надо переключать на upper-pose.
   *  В нашем GLB сюда попадает `weapone_bone` и его поддерево — это control-bone
   *  оружия, по конвенции Blender/Mixamo он держит constraint к RightHand. При
   *  экспорте в glTF constraint теряется, но запекаются TRS-каналы для каждой
   *  анимации. Без overlay weapone_bone застрянет в base-позе (idle/run), а
   *  RightHand уйдёт в firingRifle → оружие "отлипает" от руки. */
  upperBodyExtraNodes: number[];
  /** Ноды, у которых при upper-body overlay замещается ТОЛЬКО rotation
   *  (не translation/scale). Сейчас сюда попадает Hips: корпус должен
   *  всегда смотреть к прицелу при стрельбе (shoot / stance-idle overlay),
   *  но translation Hips базовой анимации сохраняется — иначе ломается
   *  вертикальный bob при ходьбе/беге, hip-level сдвигается при overlay. */
  upperBodyRotationOnlyNodes: number[];
  /** Маска per-joint для hurt-flinch'а: ВСЁ тело КРОМЕ поддеревьев
   *  `LeftUpLeg/RightUpLeg` (ноги) и `LeftShoulder/RightShoulder` (руки).
   *  Т.е. включены Hips + позвоночник (Spine/Spine1/Spine2) + шея/голова.
   *  Идея: реакция на попадание дёргает корпус, но руки продолжают держать
   *  оружие а ноги твёрдо стоят на земле. null если нет skin'а. */
  hurtMask: Uint8Array | null;
  /** Индекс ноды-«позвонка» для аим-IK (вертикальный наклон корпуса при
   *  взгляде вверх/вниз). Это, как правило, mixamorig:Spine1 — тогда наклон
   *  распределяется на всё что выше (Spine2/Neck/обе руки). null, если кость
   *  не найдена (нескинированный mesh или не Mixamo-rig). */
  aimPitchBoneIdx: number | null;
  /** Bbox по всем sub-mesh'ам (объединённо, в model space). */
  bboxMin: [number, number, number];
  bboxMax: [number, number, number];
  /** Запечённые вершины для прокси bodyVox (скин); ключ — yawOffset инстанса. */
  bodyVoxProxyBakes?: Map<number, import("./bodyVoxProxyBake").BodyVoxSkinBake>;
}

interface GltfPrimitive {
  attributes: {
    POSITION?: { value: Float32Array };
    NORMAL?: { value: Float32Array };
    TEXCOORD_0?: { value: Float32Array };
    JOINTS_0?: { value: Uint16Array | Uint8Array };
    WEIGHTS_0?: { value: Float32Array };
    COLOR_0?: { value: ArrayLike<number>; size?: number };
  };
  indices?: { value: Uint16Array | Uint32Array };
  material?: number | { pbrMetallicRoughness?: { baseColorTexture?: unknown; baseColorFactor?: number[] } };
}

/**
 * Читаем JSON-чанк из GLB до декодирования loaders.gl — нужно сохранить
 * `images[].uri` для внешнего видео (postProcess может убрать uri после загрузки).
 */
function parseGlbJsonChunk(ab: ArrayBuffer): Record<string, unknown> | null {
  const dv = new DataView(ab);
  if (dv.byteLength < 12) return null;
  let magic = "";
  for (let i = 0; i < 4; i++) magic += String.fromCharCode(dv.getUint8(i));
  if (magic !== "glTF") return null;
  let off = 12;
  while (off + 8 <= dv.byteLength) {
    const chunkLen = dv.getUint32(off, true);
    let typ = "";
    for (let i = 0; i < 4; i++) typ += String.fromCharCode(dv.getUint8(off + 4 + i));
    off += 8;
    if (typ === "JSON") {
      const jsonBytes = new Uint8Array(ab, off, chunkLen);
      const txt = new TextDecoder().decode(jsonBytes);
      try {
        return JSON.parse(txt) as Record<string, unknown>;
      } catch {
        return null;
      }
    }
    off += chunkLen;
  }
  return null;
}

/** Опции воспроизведения для ghost-видео; читаются из glTF `image.extras`,
 *  которые build_level кладёт из Custom Properties Blender-объекта `ghost_*`
 *  (см. tools/build_level/static_export.go → extractGhostVideoOptions). */
export interface GhostVideoSpec {
  uri: string;
  loop: boolean;
  speed: number;
  muted: boolean;
  autoplay: boolean;
  /** Сдвиг внутрь ролика; применяется после loadedmetadata. */
  startSec: number;
  /** Если ≥ 0 и > startSec: играем только [startSec..endSec] (loop вручную). */
  endSec: number;
}

const GHOST_VIDEO_RE = /\.(webm|mp4|mov|ogg)(\?|$)/i;

function extrasNumber(v: unknown, def: number): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const trimmed = v.trim();
    if (trimmed === "") return def;
    const n = Number(trimmed);
    if (Number.isFinite(n)) return n;
  }
  if (typeof v === "boolean") return v ? 1 : 0;
  return def;
}

function extrasBool(v: unknown, def: boolean): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    const ls = v.trim().toLowerCase();
    if (ls === "1" || ls === "true" || ls === "yes" || ls === "on") return true;
    if (ls === "0" || ls === "false" || ls === "no" || ls === "off") return false;
  }
  return def;
}

function buildGhostVideoSpec(uri: string, extras: Record<string, unknown> | null): GhostVideoSpec {
  const ex = extras ?? {};
  return {
    uri,
    loop: extrasBool(ex.video_loop, true),
    speed: Math.max(0.05, extrasNumber(ex.video_speed, 1)),
    muted: extrasBool(ex.video_muted, true),
    autoplay: extrasBool(ex.video_autoplay, true),
    startSec: Math.max(0, extrasNumber(ex.video_start_sec, 0)),
    endSec: extrasNumber(ex.video_end_sec, -1),
  };
}

/** Индекс image → spec видео-плейбэка (из исходного glTF JSON-чанка GLB). */
function tipadumaVideoSpecByImageIndex(images: unknown): Record<number, GhostVideoSpec> {
  const out: Record<number, GhostVideoSpec> = {};
  if (!Array.isArray(images)) return out;
  for (let i = 0; i < images.length; i++) {
    const im = images[i] as { uri?: string; extras?: Record<string, unknown> } | undefined;
    const u = im?.uri;
    if (typeof u === "string" && GHOST_VIDEO_RE.test(u)) {
      out[i] = buildGhostVideoSpec(u, im?.extras ?? null);
    }
  }
  return out;
}

/** GL-константа NEAREST (9728) — magFilter «Closest» из Blender. */
const GL_NEAREST = 0x2600;

/** Индекс texture → «magFilter = NEAREST» из сырого glTF JSON-чанка.
 *  Blender экспортирует Image Texture с интерполяцией «Closest» как
 *  sampler.magFilter = NEAREST — это осознанный арт-стиль (пиксель-чёткие
 *  обои/кирпич). loaders.gl после postProcess переупаковывает sampler'ы,
 *  поэтому надёжнее прочитать их напрямую из embedded JSON (как videoSpec). */
function tipadumaMagNearestByTextureIndex(root: Record<string, unknown> | null): Record<number, boolean> {
  const out: Record<number, boolean> = {};
  const textures = root?.textures;
  const samplers = root?.samplers;
  if (!Array.isArray(textures)) return out;
  for (let i = 0; i < textures.length; i++) {
    const t = textures[i] as { sampler?: number } | undefined;
    const si = t?.sampler;
    if (typeof si !== "number" || !Array.isArray(samplers)) continue;
    const s = samplers[si] as { magFilter?: number } | undefined;
    if (s?.magFilter === GL_NEAREST) out[i] = true;
  }
  return out;
}

/** WebKit не декодирует альфу VP9: в WebM она лежит ОТДЕЛЬНЫМ треком
 *  (alpha_mode=1), и WebKit его игнорирует — призрак получал непрозрачный
 *  фон. WebKit — это Safari И ЛЮБОЙ браузер на iPhone/iPad (Chrome на iOS —
 *  тоже WebKit: «CriOS» в UA, именно на нём Олег видел плоскости 21.10).
 *  HEVC-with-alpha (.mov) в WebGL на iOS ненадёжен, поэтому таким браузерам
 *  отдаём .pack.mp4: обычное H.264, верхняя половина кадра — цвет, нижняя —
 *  альфа серым; шейдер собирает их сам (uVideoPacked). */
export const VIDEO_PACKED_ALPHA = (() => {
  if (typeof navigator === "undefined") return false;
  // ?packvideo=1 — проверить путь WebKit в любом браузере.
  if (typeof location !== "undefined" && new URLSearchParams(location.search).get("packvideo") === "1") return true;
  const ua = navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1);
  const safari = /safari/i.test(ua) && !/chrome|chromium|crios|edg|android|firefox|fxios/i.test(ua);
  return ios || safari;
})();

/** .webm → .pack.mp4 для WebKit (см. VIDEO_PACKED_ALPHA). Остальным — как есть. */
export function videoUrlForBrowser(uri: string): string {
  if (!VIDEO_PACKED_ALPHA) return uri;
  return uri.replace(/\.webm(\?.*)?$/i, ".pack.mp4$1");
}

function ghostVideoUriForBrowser(uri: string): string {
  return videoUrlForBrowser(uri);
}

/** Создаёт <video> с применением spec'а: loop / speed / muted / start..end / autoplay. */
function createGhostVideoElement(spec: GhostVideoSpec): HTMLVideoElement {
  const vid = document.createElement("video");
  vid.crossOrigin = "anonymous";
  vid.muted = spec.muted;
  vid.playsInline = true;
  // preload=auto: даже при autoplay=false первый кадр должен попасть в texture
  // (renderer.bindMeshMaterial копирует текущий кадр через texImage2D, и пока
  // readyState<2 текстура остаётся 1×1 заглушкой).
  vid.preload = "auto";
  // loop: если задан endSec — делаем вручную (через timeupdate),
  // иначе используем нативный vid.loop (точнее и без gap'а в Chrome).
  const manualLoop = spec.endSec > 0 && spec.endSec > spec.startSec;
  vid.loop = spec.loop && !manualLoop;
  vid.src = ghostVideoUriForBrowser(spec.uri);
  vid.playbackRate = spec.speed;

  const onMeta = (): void => {
    if (spec.startSec > 0) {
      try { vid.currentTime = spec.startSec; } catch { /* */ }
    }
    if (spec.autoplay) void vid.play().catch(() => { /* policy */ });
  };
  if (vid.readyState >= 1) onMeta();
  else vid.addEventListener("loadedmetadata", onMeta, { once: true });
  // iOS может не начать грузить видео (и не дать loadedmetadata), пока его не
  // запустили: беззвучное inline-видео разрешено играть сразу.
  if (spec.autoplay && spec.muted) void vid.play().catch(() => { /* policy */ });

  if (manualLoop) {
    vid.addEventListener("timeupdate", () => {
      if (vid.currentTime < spec.endSec) return;
      if (spec.loop) {
        try { vid.currentTime = spec.startSec; } catch { /* */ }
      } else {
        vid.pause();
      }
    });
  }
  return vid;
}

// ─── Fallback: видео-оверлей по ИМЕНИ ноды / extras.video ────────────────────
// Основной путь видео-инжекта — build_level подставляет video-uri в image glb
// (см. tipadumaVideoSpecByImageIndex/resolveGltfImage выше). Но этот путь
// капризен: он срабатывает только для нод `ghost_*`, и после реэкспорта сцены
// из Blender ноды легко теряют префикс/extras — тогда плоскость молча остаётся
// без видео (так «отвалились» шторы Curtain_window_r1_*). Фолбэк ниже работает
// в рантайме поверх УЖЕ собранного glb: если у сабмеша нет видео из glb, но
// нода распознаётся как видео-оверлей — создаём <video> прямо в клиенте.
//
// Контракт распознавания (по приоритету):
//   1) node.extras.video — слаг ("curtains_sway"), имя файла
//      ("curtains_sway.webm") или абсолютный путь сайта ("/textures/x.webm");
//      слаг/имя резолвится в /textures/<...>.webm. Кладётся сайдкаром
//      `<scene>_ghost_extras.json` при сборке уровня (tools/build_level).
//   2) имя ноды `ghost_<slug>[_NN]` → /textures/<slug>.webm. Нормализация
//      имени: lowercase, отрезать Blender-суффикс «.001», отрезать хвост _NN.
//   3) алиасы по префиксу нормализованного имени — известные оверлеи уровней,
//      у которых имя объекта в Blender не совпадает с именем видео-файла.
// Опции плейбэка (video_loop/speed/...) читаются из extras той же ноды.
const NODE_VIDEO_ALIASES: ReadonlyArray<{ prefix: string; uri: string }> = [
  // Шторы особняка: объекты Curtain_window_r1_01/02 → curtains_sway.{webm,mov}
  { prefix: "curtain", uri: "/textures/curtains_sway.webm" },
  // Зомби-картина: на случай возвращения объекта без префикса ghost_
  { prefix: "zombi", uri: "/textures/zombi.webm" },
];

/** lowercase, без Blender-суффикса «.001» и без хвостового `_NN`. */
function normalizeOverlayNodeName(name: string): string {
  let base = name.toLowerCase();
  const dot = base.indexOf(".");
  if (dot > 0) base = base.slice(0, dot);
  base = base.replace(/_\d+$/, "");
  return base;
}

/** Сайт-абсолютный uri видео для ноды или null, если нода — не видео-оверлей. */
function fallbackVideoUriForNode(
  nodeName: string,
  nodeExtras: Record<string, unknown> | null,
): string | null {
  const exVid = nodeExtras?.video;
  if (typeof exVid === "string" && exVid.trim() !== "") {
    let v = exVid.trim();
    if (!GHOST_VIDEO_RE.test(v)) v += ".webm";
    if (!v.startsWith("/") && !/^https?:/i.test(v)) v = `/textures/${v}`;
    return v;
  }
  const base = normalizeOverlayNodeName(nodeName);
  if (base.startsWith("ghost_")) {
    const slug = base.slice("ghost_".length);
    if (slug !== "") return `/textures/${slug}.webm`;
  }
  for (const a of NODE_VIDEO_ALIASES) {
    if (base.startsWith(a.prefix)) return a.uri;
  }
  return null;
}

/**
 * Загружает GLB и собирает все mesh primitive'ы в WebGL2 ресурсы вместе со
 * скином и анимациями.
 */
export async function loadGlb(gl: WebGL2RenderingContext, url: string): Promise<LoadedMesh> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    throw new Error(`loadGlb fetch failed: ${res.status}`);
  }
  const ab = await res.arrayBuffer();
  const embedded = parseGlbJsonChunk(ab);
  const videoSpecMap = tipadumaVideoSpecByImageIndex(embedded?.images);
  const magNearestMap = tipadumaMagNearestByTextureIndex(embedded);

  // loadImages:false — иначе loaders.gl на КАЖДОЙ image.uri делает fetch+ImageLoader.parse;
  // для нашей инжектированной video-uri (`/textures/levelpacks/.../*.webm`) это:
  //   а) без `baseUrl` бросает «'baseUrl' must be provided to resolve relative url …»;
  //   б) даже с baseUrl ImageLoader не умеет webm и упадёт.
  // Любая такая ошибка АБОРТИТ весь GLB-парсинг → static_meshes исчезает целиком
  // (мы это видели как «пропал особняк, остался только лабиринт»).
  // postProcessGLTF всё равно резолвит `image.bufferView → bufferView.data`,
  // а embedded PNG/JPEG ниже декодируются через decodeRawImage (createImageBitmap),
  // так что отключение здесь безопасно.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw: any = await load(ab, GLTFLoader, {
    gltf: { loadImages: false, loadBuffers: true, decompressMeshes: true },
    // weapons: модели оружия сжаты Draco — декодер свой (public/draco), без
    // CDN и без воркера (loaders.gl по умолчанию тянет их с unpkg/gstatic).
    worker: false,
    useLocalLibraries: true,
    modules: {
      "draco_wasm_wrapper.js": "/draco/draco_wasm_wrapper.js",
      "draco_decoder.wasm": "/draco/draco_decoder.wasm",
    },
    draco: { decoderType: "wasm" },
  } as any);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gltf: any = postProcessGLTF(raw);
  gltf.userData = {
    ...(gltf.userData ?? {}),
    tipadumaVideoSpecByImageIndex: videoSpecMap,
    tipadumaMagNearestByTextureIndex: magNearestMap,
  };

  console.log("[mesh] gltf structure:", {
    meshes: gltf.meshes?.length ?? 0,
    nodes: gltf.nodes?.length ?? 0,
    materials: gltf.materials?.length ?? 0,
    textures: gltf.textures?.length ?? 0,
    images: gltf.images?.length ?? 0,
    skins: gltf.skins?.length ?? 0,
    animations: gltf.animations?.length ?? 0,
  });

  if (!gltf.meshes || gltf.meshes.length === 0) {
    throw new Error("GLB has no meshes");
  }

  // ─── Node tree ───────────────────────────────────────────────────────
  const nodes = parseNodes(gltf);
  const nodeTraversalOrder = buildTraversalOrder(nodes);

  // ─── Skin (берём первый, обычно один) ────────────────────────────────
  const skin = parseFirstSkin(gltf);

  // ─── Sub-meshes: проходим ВСЕ nodes, рендерим каждый primitive ──────
  const subMeshes: SubMesh[] = [];
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  // Дедуплицируем GPU-текстуры: одна материал-текстура → одна загрузка.
  const texCache = newMaterialTextureCache();
  const pendingDecodes: Promise<void>[] = [];

  for (let nodeIdx = 0; nodeIdx < nodes.length; nodeIdx++) {
    const n = nodes[nodeIdx];
    if (n.meshIdx < 0) continue;
    const mesh = gltf.meshes[n.meshIdx];
    if (!mesh.primitives) continue;
    for (const prim of mesh.primitives as GltfPrimitive[]) {
      const sub = buildSubMesh(gl, gltf, prim, nodeIdx, n.name, texCache, pendingDecodes);
      if (!sub) continue;
      subMeshes.push(sub.subMesh);
      // bbox aggregate (POSITION в model space, без node transform — для
      // вычисления масштаба персонажа этого достаточно: skinned mesh уже
      // в правильной позе после bind, weapon хоть и привязан к hand, но
      // его позиция влияет лишь на прицел камеры в FP, не на масштаб).
      if (sub.bboxMin[0] < minX) minX = sub.bboxMin[0];
      if (sub.bboxMin[1] < minY) minY = sub.bboxMin[1];
      if (sub.bboxMin[2] < minZ) minZ = sub.bboxMin[2];
      if (sub.bboxMax[0] > maxX) maxX = sub.bboxMax[0];
      if (sub.bboxMax[1] > maxY) maxY = sub.bboxMax[1];
      if (sub.bboxMax[2] > maxZ) maxZ = sub.bboxMax[2];
    }
  }

  if (subMeshes.length === 0) throw new Error("GLB has no primitives");

  // Дождаться декодирование сырых PNG/JPEG из bufferView (loaders.gl не всегда
  // декодирует «горячо» — без await placeholder остался бы серым).
  if (pendingDecodes.length > 0) {
    await Promise.all(pendingDecodes);
  }

  // ─── Animations с whitelist имён ────────────────────────────────────
  const animations = parseAnimations(gltf);
  // 1) Обрезаем начало "длинных" клипов (например runToStop — оставляем
  //    только финальный тормозной шаг, а не весь fade-out из бега).
  for (const [name, cutoff] of Object.entries(CLIP_TRIM_START)) {
    const clip = animations.get(name);
    if (clip) {
      const orig = clip.duration;
      trimClipStart(clip, cutoff);
      console.log(`[mesh] trimmed clip '${name}': ${orig.toFixed(2)}s → ${clip.duration.toFixed(2)}s (cutoff=${cutoff}s)`);
    }
  }
  // 2) Фиксируем Hips у in-place клипов на baseline целевой "стабильной"
  //    анимации (idle). Идея: runToStop играет и в начале, и в конце при
  //    одинаковом Hips = idle.Hips — значит переход runToStop → idle
  //    проходит без Hips-сдвига (шов невидимый). Единственный оставшийся
  //    шов — run → runToStop, и он размазан на 150мс кроссфейда.
  const idleHips = (() => {
    const idleClip = animations.get("idle");
    return idleClip ? getHipsFirstTranslation(idleClip, nodes) : null;
  })();
  for (const clipName of IN_PLACE_CLIPS) {
    const clip = animations.get(clipName);
    if (!clip) continue;
    if (idleHips) {
      pinHipsToConstant(clip, nodes, idleHips[0], idleHips[1], idleHips[2]);
      console.log(`[mesh] pinned '${clipName}' Hips to idle baseline (${idleHips.map((v) => v.toFixed(2)).join(",")})`);
    }
  }

  // ─── Upper-body mask ────────────────────────────────────────────────
  let upperBodyMask: Uint8Array | null = null;
  if (skin) {
    upperBodyMask = buildUpperBodyMask(nodes, skin.joints, UPPER_BODY_CUTOFF);
    const upperCount = upperBodyMask.reduce<number>((s, v) => s + v, 0);
    console.log(`[mesh] upper-body mask: ${upperCount}/${skin.joints.length} joints (cutoff=${UPPER_BODY_CUTOFF})`);
  }

  // ─── Upper-body extra nodes (weapon control bones) ──────────────────
  // Blender часто использует "control bones" типа weapone_bone, которые
  // удерживают оружие в руке через constraint. В glTF constraint не
  // переезжает, остаются только запечённые TRS-каналы анимации. Чтобы
  // оружие следовало за рукой при overlay-стрельбе, надо насильно перевести
  // эти ноды на upper-pose вместе с upper-body subtree.
  const upperBodyExtraNodes = collectExtraOverlayNodes(nodes, [
    "weapone_bone", "weapon_bone", "WeaponBone",
  ]);
  if (upperBodyExtraNodes.length > 0) {
    const names = upperBodyExtraNodes.map((i) => nodes[i].name).join(", ");
    console.log(`[mesh] upper-body extra nodes (control bones): [${names}]`);
  }

  // ─── Rotation-only override nodes (Hips) ─────────────────────────────
  // Hips не в upper-mask (иначе translation Hips заменится и сломается
  // вертикальный bob при беге). Но strafe/forward-strafe клипы имеют
  // Mixamo-авторский Hips yaw до ±150°, который крутит весь torso.
  // Решение: при default upper-body overlay заменяем у Hips ТОЛЬКО
  // rotation — translation остаётся из base (bob сохраняется), а корпус
  // подтягивается к aim-forward стойке stance/shoot.
  const upperBodyRotationOnlyNodes: number[] = [];
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].name === "mixamorig:Hips") {
      upperBodyRotationOnlyNodes.push(i);
      console.log(`[mesh] upper-body rotation-only: node ${i} (${nodes[i].name})`);
      break;
    }
  }

  // ─── Hurt mask (верх корпуса + голова, БЕЗ рук, ног и оружия) ────────
  // Используется для hurt-flinch overlay на NPC: верх корпуса дёргается,
  // а руки (держат оружие), ноги (стоят на земле) и `weapone_bone` (автомат)
  // остаются в idle.
  //
  // Тонкость #1: `weapone_bone` в нашем GLB ПОПАЛА в `skin.joints`
  // (как Bone-Parent control, index ≈ 31). Если не занулить явно — общая
  // маска (fill(1)) её включает, overlay перезаписывает её local TRS
  // hurt-значением, автомат "подпрыгивает" на каждый хит.
  //
  // Тонкость #2: исключаем также `mixamorig:Hips` и `mixamorig:Spine`
  // (индивидуально, не subtree). Причина: `weapone_bone` — child `Spine`
  // в node tree. Даже если сам weapone_bone не overlay-ится, поворот
  // Spine по FK передаётся ему через world-matrix. Замораживая Hips и
  // Spine в idle, получаем стабильный world для автомата; hurt же
  // продолжает читаться на Spine1/Spine2/Neck/Head.
  let hurtMask: Uint8Array | null = null;
  if (skin) {
    const exclude = findJointSubtreeIndices(nodes, skin.joints, [
      "mixamorig:LeftUpLeg", "mixamorig:RightUpLeg",
      "mixamorig:LeftShoulder", "mixamorig:RightShoulder",
    ]);
    hurtMask = new Uint8Array(skin.joints.length).fill(1);
    for (const j of exclude) hurtMask[j] = 0;
    // Индивидуальные кости — не subtree (иначе снимем Spine1+, что уберёт
    // весь flinch). weapon-имена добавлены на случай, если в каких-то
    // версиях меша они другие (weapon_bone / WeaponBone).
    const excludeIndividual = new Set<string>([
      "mixamorig:Hips", "mixamorig:Spine",
      "weapone_bone", "weapon_bone", "WeaponBone", "weapon",
    ]);
    for (let j = 0; j < skin.joints.length; j++) {
      if (excludeIndividual.has(nodes[skin.joints[j]].name)) hurtMask[j] = 0;
    }
    const included = hurtMask.reduce<number>((s, v) => s + v, 0);
    console.log(`[mesh] hurt mask: ${included}/${skin.joints.length} joints (upper torso + head, weapon & Hips/Spine frozen)`);
  }

  // ─── Aim pitch bone (для look-up/down IK) ────────────────────────────
  // Берём самый нижний spine — `mixamorig:Spine`. Это lowest common ancestor
  // и upper-body subtree (Spine1+) и weapone_bone (control-кость оружия).
  // Если крутить Spine1, weapone_bone не повернётся (она sibling Spine1
  // под Spine), и оружие "отлипнет" от руки при aim up/down.
  let aimPitchBoneIdx: number | null = null;
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].name === "mixamorig:Spine") { aimPitchBoneIdx = i; break; }
  }
  if (aimPitchBoneIdx !== null) {
    console.log(`[mesh] aim-pitch bone = node ${aimPitchBoneIdx} (${nodes[aimPitchBoneIdx].name})`);
  }

  console.log(
    `[mesh] ${url}: ${subMeshes.length} sub-mesh${subMeshes.length === 1 ? "" : "es"}, ` +
    `skin=${skin ? skin.joints.length + " joints" : "none"}, ` +
    `animations=[${[...animations.keys()].join(", ")}], ` +
    `bbox (${minX.toFixed(2)},${minY.toFixed(2)},${minZ.toFixed(2)})..` +
    `(${maxX.toFixed(2)},${maxY.toFixed(2)},${maxZ.toFixed(2)})`,
  );

  return {
    subMeshes,
    skin,
    nodes,
    nodeTraversalOrder,
    animations,
    upperBodyMask,
    upperBodyExtraNodes,
    upperBodyRotationOnlyNodes,
    hurtMask,
    aimPitchBoneIdx,
    bboxMin: [minX, minY, minZ],
    bboxMax: [maxX, maxY, maxZ],
  };
}

/** Найти ноды по списку имён + добавить их subtree (для weapon control bones,
 *  которые держат оружие via Blender constraints). */
function collectExtraOverlayNodes(nodes: NodeData[], names: string[]): number[] {
  const nameSet = new Set(names);
  const out = new Set<number>();
  for (let i = 0; i < nodes.length; i++) {
    if (!nameSet.has(nodes[i].name)) continue;
    const stack = [i];
    while (stack.length > 0) {
      const k = stack.pop()!;
      if (out.has(k)) continue;
      out.add(k);
      for (const c of nodes[k].children) stack.push(c);
    }
  }
  return [...out];
}

/** Возвращает индексы нод в порядке, гарантирующем, что родитель встречается
 *  раньше своих потомков (DFS от корней). Поддерживает несколько корней.
 *
 *  Это нужно потому что glTF не требует топологического порядка в `nodes[]`.
 *  В частности, Blender при экспорте из FBX кладёт корневую `Armature`
 *  в самый конец массива, а её дочерние ноды (Hips, mesh) — раньше. Если
 *  вычислять FK строго по индексу `0..n-1`, у скелета пропадёт глобальный
 *  Armature TRS (Rx(90°)·Scale(0.01) для Mixamo) — все кости окажутся
 *  в локальном frame, IBM с компенсирующим scale=100 даст вершины в ~100×
 *  размере, и в кадр попадёт только небольшой кусок гигантского меша. */
function buildTraversalOrder(nodes: NodeData[]): number[] {
  const out: number[] = [];
  const visited = new Uint8Array(nodes.length);
  const dfs = (root: number): void => {
    const stack = [root];
    while (stack.length > 0) {
      const i = stack.pop()!;
      if (visited[i]) continue;
      visited[i] = 1;
      out.push(i);
      // children в обратном порядке, чтобы DFS обходил их слева-направо
      const ch = nodes[i].children;
      for (let k = ch.length - 1; k >= 0; k--) stack.push(ch[k]);
    }
  };
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].parent < 0 && !visited[i]) dfs(i);
  }
  // Сиротские узлы (parent != -1, но parent тоже сирота / цикл) — на всякий
  // случай дотолкаем в конец, чтобы не потерять матрицы.
  for (let i = 0; i < nodes.length; i++) {
    if (!visited[i]) dfs(i);
  }
  return out;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseNodes(gltf: any): NodeData[] {
  const rawNodes = gltf.nodes ?? [];
  // postProcessGLTF РЕЗОЛВИТ ссылки в объекты: n.children — это array of node
  // objects (а не indices), n.mesh — mesh object, и т.п. Мы конвертим обратно
  // в индексы через identity-поиск.
  const nodes: NodeData[] = rawNodes.map((n: { name?: string; children?: unknown[]; translation?: number[]; rotation?: number[]; scale?: number[]; mesh?: unknown }, i: number) => ({
    name: n.name ?? `node${i}`,
    parent: -1,
    children: (n.children ?? []).map((c) => resolveNodeIndex(gltf, c)).filter((c: number) => c >= 0),
    translation: (n.translation as [number, number, number] | undefined) ?? [0, 0, 0],
    rotation: (n.rotation as [number, number, number, number] | undefined) ?? [0, 0, 0, 1],
    scale: (n.scale as [number, number, number] | undefined) ?? [1, 1, 1],
    meshIdx: resolveMeshIndex(gltf, n.mesh),
  }));
  for (let i = 0; i < nodes.length; i++) {
    for (const c of nodes[i].children) {
      if (c >= 0 && c < nodes.length) nodes[c].parent = i;
    }
  }
  return nodes;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolveMeshIndex(gltf: any, meshRef: unknown): number {
  if (meshRef === undefined || meshRef === null) return -1;
  if (typeof meshRef === "number") return meshRef;
  // postProcessGLTF может разрезолвить в объект — найдём индекс identity'ом
  const meshes = gltf.meshes ?? [];
  for (let i = 0; i < meshes.length; i++) if (meshes[i] === meshRef) return i;
  return -1;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseFirstSkin(gltf: any): SkinData | null {
  if (!gltf.skins || gltf.skins.length === 0) return null;
  const skin = gltf.skins[0];
  // joints: либо number[], либо array of node objects
  const rawJoints = skin.joints ?? [];
  const joints: number[] = rawJoints.map((j: unknown) => {
    if (typeof j === "number") return j;
    return resolveNodeIndex(gltf, j);
  }).filter((i: number) => i >= 0);

  // inverseBindMatrices: accessor с size=16, count=joints.length
  let ibm: Float32Array;
  const ibmRef = skin.inverseBindMatrices;
  if (ibmRef && typeof ibmRef === "object" && "value" in ibmRef) {
    ibm = (ibmRef as { value: Float32Array }).value;
  } else if (typeof ibmRef === "number") {
    const acc = gltf.accessors[ibmRef];
    ibm = acc.value as Float32Array;
  } else {
    // Без IBM используем identity
    ibm = new Float32Array(joints.length * 16);
    for (let i = 0; i < joints.length; i++) {
      ibm[i * 16 + 0] = 1; ibm[i * 16 + 5] = 1; ibm[i * 16 + 10] = 1; ibm[i * 16 + 15] = 1;
    }
  }
  return { joints, inverseBindMatrices: ibm };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolveNodeIndex(gltf: any, nodeRef: unknown): number {
  if (typeof nodeRef === "number") return nodeRef;
  const nodes = gltf.nodes ?? [];
  for (let i = 0; i < nodes.length; i++) if (nodes[i] === nodeRef) return i;
  return -1;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GltfAnimRaw = { name?: string; channels: unknown[]; samplers: unknown[] };

/** Один glTF animation → наш AnimationClip (семантическое имя для Map). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildAnimationClipFromGltfAnim(
  gltf: any, anim: GltfAnimRaw, semantic: string,
): AnimationClip | null {
  const channels: AnimationChannel[] = [];
  let duration = 0;
  for (const rawCh of anim.channels) {
    const ch = rawCh as { target: { node: unknown; path: AnimationPath }; sampler: unknown };
    const target = ch.target;
    if (!target || target.path === undefined) continue;
    if (target.path !== "translation" && target.path !== "rotation" && target.path !== "scale") continue;
    const targetNode = resolveNodeIndex(gltf, target.node);
    if (targetNode < 0) continue;

    let sampler: { input: unknown; output: unknown; interpolation?: string };
    if (typeof ch.sampler === "number") {
      sampler = anim.samplers[ch.sampler] as typeof sampler;
    } else {
      sampler = ch.sampler as typeof sampler;
    }

    const times = resolveAccessorFloat(gltf, sampler.input);
    const values = resolveAccessorFloat(gltf, sampler.output);
    if (!times || !values) continue;
    const components: 3 | 4 = target.path === "rotation" ? 4 : 3;
    channels.push({ targetNode, path: target.path, times, values, components });
    const last = times[times.length - 1];
    if (last > duration) duration = last;
  }
  if (channels.length === 0) return null;
  return { name: semantic, duration, channels };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseAnimations(gltf: any): Map<string, AnimationClip> {
  const out = new Map<string, AnimationClip>();
  if (!gltf.animations) return out;
  const anims = gltf.animations as GltfAnimRaw[];
  for (const anim of anims) {
    const semantic = ANIM_NAME_MAP[anim.name ?? ""];
    if (!semantic) continue;
    if (out.has(semantic)) continue;
    const clip = buildAnimationClipFromGltfAnim(gltf, anim, semantic);
    if (clip) out.set(semantic, clip);
  }
  // Пропсы (ammo/medkit): клипы без ANIM_NAME_MAP — первый валидный → "idle" (луп вращения и т.п.).
  if (!out.has("idle")) {
    for (const anim of anims) {
      const clip = buildAnimationClipFromGltfAnim(gltf, anim, "idle");
      if (clip) {
        out.set("idle", clip);
        break;
      }
    }
  }
  return out;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolveAccessorFloat(gltf: any, accRef: unknown): Float32Array | null {
  if (!accRef) return null;
  if (typeof accRef === "object" && "value" in (accRef as object)) {
    return (accRef as { value: Float32Array }).value;
  }
  if (typeof accRef === "number") {
    const acc = gltf.accessors?.[accRef];
    return acc?.value ?? null;
  }
  return null;
}

/** Возвращает индексы skin-joint'ов (в порядке skin.joints) которые соответствуют
 *  поддереву нод с заданными именами. Удобно для FP head-cull: передать
 *  ["mixamorig:Head", "mixamorig:Neck"] → получить набор joint indices. */
export function findJointSubtreeIndices(
  nodes: NodeData[], skinJoints: number[], rootNames: string[],
): number[] {
  const subtree = new Set<number>();
  for (const name of rootNames) {
    let rootIdx = -1;
    for (let i = 0; i < nodes.length; i++) if (nodes[i].name === name) { rootIdx = i; break; }
    if (rootIdx < 0) continue;
    const stack = [rootIdx];
    while (stack.length > 0) {
      const i = stack.pop()!;
      if (subtree.has(i)) continue;
      subtree.add(i);
      for (const c of nodes[i].children) stack.push(c);
    }
  }
  const out: number[] = [];
  for (let j = 0; j < skinJoints.length; j++) {
    if (subtree.has(skinJoints[j])) out.push(j);
  }
  return out;
}

/** Одинаковые имена нод и порядок skin.joints — нужно для второго меша (воксель) с общей позой. */
export function skeletonsCompatible(outer: LoadedMesh, inner: LoadedMesh): boolean {
  if (outer.nodes.length !== inner.nodes.length) return false;
  for (let i = 0; i < outer.nodes.length; i++) {
    if (outer.nodes[i]!.name !== inner.nodes[i]!.name) return false;
  }
  const os = outer.skin;
  const ins = inner.skin;
  if (!os || !ins || os.joints.length !== ins.joints.length) return false;
  for (let j = 0; j < os.joints.length; j++) {
    if (os.joints[j] !== ins.joints[j]) return false;
  }
  return true;
}

/** Каждая кость inner.skin есть в outer по имени — тогда поза outer FK подходит для inner IBM (индексы нод могут отличаться). */
export function canSkinInnerToOuterPose(outer: LoadedMesh, inner: LoadedMesh): boolean {
  const s = inner.skin;
  if (!s) return false;
  const outerNames = new Set(outer.nodes.map((n) => n.name));
  for (let j = 0; j < s.joints.length; j++) {
    const ni = s.joints[j];
    if (ni < 0 || ni >= inner.nodes.length) return false;
    if (!outerNames.has(inner.nodes[ni]!.name)) return false;
  }
  return true;
}

function buildUpperBodyMask(nodes: NodeData[], skinJoints: number[], cutoffName: string): Uint8Array {
  const out = new Uint8Array(skinJoints.length);
  let cutoffIdx = -1;
  for (let i = 0; i < nodes.length; i++) if (nodes[i].name === cutoffName) { cutoffIdx = i; break; }
  if (cutoffIdx < 0) {
    console.warn(`[mesh] upper-body cutoff '${cutoffName}' not found, mask is empty`);
    return out;
  }
  // Subtree: BFS по children
  const subtree = new Set<number>();
  const stack = [cutoffIdx];
  while (stack.length > 0) {
    const i = stack.pop()!;
    if (subtree.has(i)) continue;
    subtree.add(i);
    for (const c of nodes[i].children) stack.push(c);
  }
  for (let j = 0; j < skinJoints.length; j++) {
    if (subtree.has(skinJoints[j])) out[j] = 1;
  }
  return out;
}

interface BuildSubMeshResult {
  subMesh: SubMesh;
  bboxMin: [number, number, number];
  bboxMax: [number, number, number];
}

/** Разбор строки слоя из glTF extras (несколько возможных ключей/значений). */
function bodyLayerFromExtrasObject(ex: unknown): "outer" | "inner" | null {
  if (!ex || typeof ex !== "object") return null;
  const o = ex as Record<string, unknown>;
  const keys = ["TIPADUMA_BODY_LAYER", "body_layer", "BodyLayer", "tipaduma_body_layer", "layer"];
  for (const key of keys) {
    const v = o[key];
    if (typeof v !== "string") continue;
    const low = v.toLowerCase();
    if (low === "outer") return "outer";
    if (low === "inner" || low === "voxel") return "inner";
  }
  return null;
}

/** material / primitive extras + имя ноды (часто меш «voxel» без кастомных extras). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolveBodyLayer(gltf: any, prim: GltfPrimitive, nodeName: string): "outer" | "inner" | null {
  const m: unknown = prim.material;
  if (m !== undefined && m !== null) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mat: any = typeof m === "number" ? gltf.materials?.[m] : m;
    const hit = bodyLayerFromExtrasObject(mat?.extras);
    if (hit) return hit;
    const matName = typeof mat?.name === "string" ? mat.name.toLowerCase() : "";
    if (matName.includes("voxel")) return "inner";
    if (matName.includes("outer") || matName.includes("smooth") || matName.includes("shell")) return "outer";
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const primEx = (prim as { extras?: unknown }).extras;
  const hitP = bodyLayerFromExtrasObject(primEx);
  if (hitP) return hitP;

  const nlow = nodeName.toLowerCase();
  if (nlow.includes("voxel")) return "inner";
  // Mixamo/Blender: основной меш часто называется «output» — это гладкая оболочка
  // рядом с отдельной нодой «voxel». Без этого слой остаётся null и рисуются все меши.
  if (nlow === "output") return "outer";
  if (nlow.includes("outer") || nlow.includes("smooth") || nlow.includes("shell")) return "outer";
  return null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function readBaseColorFactor(
  gltf: any, prim: GltfPrimitive,
): [number, number, number, number] {
  const def: [number, number, number, number] = [1, 1, 1, 1];
  if (prim.material === undefined || prim.material === null) return def;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mat: any = typeof prim.material === "number"
    ? gltf.materials?.[prim.material]
    : prim.material;
  const f = mat?.pbrMetallicRoughness?.baseColorFactor;
  if (!Array.isArray(f) || f.length < 3) return def;
  return [
    Number(f[0]),
    Number(f[1]),
    Number(f[2]),
    f.length >= 4 && Number.isFinite(Number(f[3])) ? Number(f[3]) : 1,
  ];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getPrimitiveMaterial(gltf: any, prim: GltfPrimitive): any | null {
  if (prim.material === undefined || prim.material === null) return null;
  return typeof prim.material === "number"
    ? gltf.materials?.[prim.material]
    : prim.material;
}

/** Источник + index, чтобы можно было дедуплицировать GPU-текстуры на загрузку. */
interface ResolvedGltfImage {
  /** Готовая для texImage2D картинка (ImageBitmap/HTMLImage/HTMLVideo/HTMLCanvas) или null. */
  image: TexImageSource | null;
  /** Сырые байты image.bufferView (если loaders.gl не декодировал картинку сам). */
  raw: Uint8Array | null;
  rawMime: string;
  /** Идентификатор для дедупа (например `idx:7`). */
  cacheKey: string;
  /** glTF sampler.magFilter == NEAREST («Closest» в Blender). */
  magNearest: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function pickImageLike(obj: any): TexImageSource | null {
  if (!obj || typeof obj !== "object") return null;
  if (obj instanceof ImageBitmap || obj instanceof HTMLImageElement
      || obj instanceof HTMLVideoElement || obj instanceof HTMLCanvasElement
      || obj instanceof OffscreenCanvas) {
    return obj as unknown as TexImageSource;
  }
  if (typeof obj.width === "number" && typeof obj.height === "number"
      && obj.width > 0 && obj.height > 0
      && (obj.data || obj.bitmap || obj.image)) {
    if (obj.bitmap instanceof ImageBitmap) return obj.bitmap;
    if (obj.image instanceof ImageBitmap || obj.image instanceof HTMLImageElement) return obj.image;
  }
  if (obj.image && (obj.image instanceof ImageBitmap || obj.image instanceof HTMLImageElement)) {
    return obj.image as TexImageSource;
  }
  if (obj.bitmap instanceof ImageBitmap) return obj.bitmap as TexImageSource;
  return null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolveGltfImage(gltf: any, texInfo: any): ResolvedGltfImage | null {
  if (!texInfo) return null;
  const texIdx = typeof texInfo.index === "number" ? texInfo.index : null;

  const vidMap = gltf.userData?.tipadumaVideoSpecByImageIndex as
    Record<number, GhostVideoSpec> | undefined;
  if (texIdx !== null && vidMap && gltf.textures?.[texIdx]) {
    const src = gltf.textures[texIdx].source;
    const imgIx = typeof src === "number" ? src : null;
    if (imgIx !== null && vidMap[imgIx]) {
      const spec = vidMap[imgIx];
      const origin =
        typeof window !== "undefined" && window.location?.origin ? window.location.origin : "";
      const absUri = spec.uri.startsWith("/") ? `${origin}${spec.uri}` : spec.uri;
      const vid = createGhostVideoElement({ ...spec, uri: absUri });
      const ck = `idx:${texIdx}`;
      return { image: vid, raw: null, rawMime: "", cacheKey: ck, magNearest: false };
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let txObj: any = texInfo.texture ?? null;
  if (!txObj && texIdx !== null && gltf.textures?.[texIdx]) {
    txObj = gltf.textures[texIdx];
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let imgObj: any = txObj?.source ?? null;
  if (imgObj && typeof imgObj === "number" && gltf.images?.[imgObj]) {
    imgObj = gltf.images[imgObj];
  }
  if (!imgObj) imgObj = texInfo.source ?? texInfo.image ?? null;

  // Внешнее видео по image.uri (glTF image с абсолютным путём сайта, напр. /textures/zombi.webm).
  if (imgObj && typeof imgObj.uri === "string") {
    const uri = imgObj.uri;
    if (GHOST_VIDEO_RE.test(uri)) {
      const origin =
        typeof window !== "undefined" && window.location?.origin ? window.location.origin : "";
      const absUri = uri.startsWith("/") ? `${origin}${uri}` : uri;
      const extras = (imgObj as { extras?: Record<string, unknown> }).extras ?? null;
      const spec = buildGhostVideoSpec(absUri, extras);
      const vid = createGhostVideoElement(spec);
      const ck = texIdx !== null ? `idx:${texIdx}` : `vid:${uri}`;
      return { image: vid, raw: null, rawMime: "", cacheKey: ck, magNearest: false };
    }
  }

  const direct = pickImageLike(imgObj) ?? pickImageLike(imgObj?.image) ?? pickImageLike(texInfo);
  let raw: Uint8Array | null = null;
  let mime = "";
  if (!direct && imgObj) {
    if (imgObj.bufferView && imgObj.bufferView.data instanceof Uint8Array) {
      raw = imgObj.bufferView.data as Uint8Array;
    } else if (imgObj.data instanceof Uint8Array) {
      raw = imgObj.data as Uint8Array;
    }
    if (typeof imgObj.mimeType === "string") mime = imgObj.mimeType;
  }
  const cacheKey = texIdx !== null ? `idx:${texIdx}` : `obj:${imgObj?.name ?? Math.random()}`;
  if (!direct && !raw) return null;
  const magNearest = texIdx !== null
    && (gltf.userData?.tipadumaMagNearestByTextureIndex as Record<number, boolean> | undefined)
      ?.[texIdx] === true;
  return { image: direct, raw, rawMime: mime, cacheKey, magNearest };
}

/** Параметры сэмплинга для non-video glb-текстур (после texImage2D):
 *  трилинейные мипы + REPEAT + анизотропия ×8 (паритет с uploadGLTexture
 *  для levelpack-чанков — без неё стены под острым углом мылятся сильнее,
 *  чем в Blender). magNearest — «Closest» из Blender-сэмплера (пиксель-арт
 *  обоев/кирпича): mag=NEAREST, но min оставляем LINEAR_MIPMAP_LINEAR —
 *  запрошенный glTF NEAREST_MIPMAP_NEAREST даёт резкие скачки/мерцание
 *  между мип-уровнями, а «как в Blender» вблизи определяет именно mag. */
function applyMeshTextureSampling(gl: WebGL2RenderingContext, magNearest: boolean): void {
  gl.generateMipmap(gl.TEXTURE_2D);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, magNearest ? gl.NEAREST : gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
  const ext = gl.getExtension("EXT_texture_filter_anisotropic");
  if (ext) {
    const max = gl.getParameter(ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT) as number;
    gl.texParameterf(gl.TEXTURE_2D, ext.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(8, max));
  }
}

/** Цвет мешей (baseColor и видеотекстуры) грузим БЕЗ sRGB-декодирования —
 *  так же, как текстуры воксельных чанков. Освещение и выход у движка
 *  без гамма-коррекции, и декодирование делало меши (стены дома, полы, мебель,
 *  персонажи) примерно в 2,15 раза темнее воксельной стены с той же текстурой. */
export const MESH_COLOR_SRGB =
  typeof location !== "undefined" && new URLSearchParams(location.search).get("meshsrgb") === "1"; // для сравнения со старым видом

function uploadTexture2D(
  gl: WebGL2RenderingContext,
  img: TexImageSource,
  opts: { label: string; srgb: boolean; video?: boolean; magNearest?: boolean },
): WebGLTexture | null {
  const tex = gl.createTexture();
  if (!tex) return null;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  try {
    const internalFmt = opts.srgb ? (gl.SRGB8_ALPHA8 ?? gl.RGBA8) : gl.RGBA8;
    const fmt = opts.srgb ? gl.RGBA : gl.RGBA;
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
    const isVid = opts.video === true || img instanceof HTMLVideoElement;
    if (isVid && img instanceof HTMLVideoElement && img.readyState < 2) {
      gl.texImage2D(
        gl.TEXTURE_2D, 0, internalFmt, 1, 1, 0, fmt, gl.UNSIGNED_BYTE,
        new Uint8Array([0, 0, 0, 0]),
      );
    } else {
      gl.texImage2D(gl.TEXTURE_2D, 0, internalFmt, fmt, gl.UNSIGNED_BYTE, img);
    }
    if (isVid) {
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    } else {
      applyMeshTextureSampling(gl, opts.magNearest === true);
    }
    return tex;
  } catch (e) {
    console.warn(`[mesh] failed to upload texture (${opts.label}):`, e);
    gl.deleteTexture(tex);
    return null;
  }
}

/**
 * Декодировать сырые байты image (PNG/JPEG из bufferView) через createImageBitmap.
 * Возвращает Promise<ImageBitmap>; до его готовности на месте текстуры — null.
 * Для асинхронности достаточно один раз вызвать decode и записать в кэш.
 */
async function decodeRawImage(raw: Uint8Array, mime: string): Promise<ImageBitmap | null> {
  try {
    const ab = new ArrayBuffer(raw.byteLength);
    new Uint8Array(ab).set(raw);
    const blob = new Blob([ab], { type: mime || "image/png" });
    return await createImageBitmap(blob);
  } catch (e) {
    console.warn("[mesh] decodeRawImage failed:", e);
    return null;
  }
}

interface CachedGpuTex {
  tex: WebGLTexture | null;
  /** Только для diffuse video baseColor. */
  video: HTMLVideoElement | null;
}

interface MaterialTextureCache {
  diffuse: Map<string, CachedGpuTex>;
  normal: Map<string, CachedGpuTex>;
  metallicRoughness: Map<string, CachedGpuTex>;
}

function newMaterialTextureCache(): MaterialTextureCache {
  return {
    diffuse: new Map(),
    normal: new Map(),
    metallicRoughness: new Map(),
  };
}

function loadTextureFromMaterialInfoCached(
  gl: WebGL2RenderingContext,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gltf: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  texInfo: any,
  label: string,
  srgb: boolean,
  cache: Map<string, CachedGpuTex>,
  pendingDecodes: Promise<void>[],
): [WebGLTexture | null, HTMLVideoElement | null] {
  const r = resolveGltfImage(gltf, texInfo);
  if (!r) return [null, null];
  const hit = cache.get(r.cacheKey);
  if (hit) return [hit.tex, hit.video];
  if (r.image) {
    const isVid = r.image instanceof HTMLVideoElement;
    const vid: HTMLVideoElement | null = isVid ? (r.image as HTMLVideoElement) : null;
    const tex = uploadTexture2D(gl, r.image, { label, srgb, video: isVid, magNearest: r.magNearest });
    cache.set(r.cacheKey, { tex, video: vid });
    if (isVid && vid) {
      pendingDecodes.push((async () => {
        const v = vid;
        // Не держим загрузку уровня: iOS может не грузить видео до play() или
        // молча не открыть файл — тогда «loadeddata» не придёт никогда, и игра
        // висела на «Loading textures… 95%» (21.10). Ошибка или 4 с — идём
        // дальше; кадр подхватит renderer.bindMeshMaterial, как только появится.
        const ready = await new Promise<boolean>((res) => {
          if (v.readyState >= 2) { res(true); return; }
          const t = setTimeout(() => res(false), 4000);
          v.addEventListener("loadeddata", () => { clearTimeout(t); res(true); }, { once: true });
          v.addEventListener("error", () => { clearTimeout(t); res(false); }, { once: true });
        });
        if (!tex || !ready) return;
        gl.bindTexture(gl.TEXTURE_2D, tex);
        const internalFmt = srgb ? (gl.SRGB8_ALPHA8 ?? gl.RGBA8) : gl.RGBA8;
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.texImage2D(gl.TEXTURE_2D, 0, internalFmt, gl.RGBA, gl.UNSIGNED_BYTE, v);
      })());
    }
    return [tex, vid];
  }
  if (r.raw) {
    const placeholder = gl.createTexture();
    if (!placeholder) return [null, null];
    gl.bindTexture(gl.TEXTURE_2D, placeholder);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
      new Uint8Array(srgb ? [200, 200, 200, 255] : [128, 128, 255, 255]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    cache.set(r.cacheKey, { tex: placeholder, video: null });
    pendingDecodes.push((async () => {
      const img = await decodeRawImage(r.raw!, r.rawMime);
      if (!img) return;
      gl.bindTexture(gl.TEXTURE_2D, placeholder);
      const internalFmt = srgb ? (gl.SRGB8_ALPHA8 ?? gl.RGBA8) : gl.RGBA8;
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
      gl.texImage2D(gl.TEXTURE_2D, 0, internalFmt, gl.RGBA, gl.UNSIGNED_BYTE, img);
      applyMeshTextureSampling(gl, r.magNearest);
    })());
    return [placeholder, null];
  }
  cache.set(r.cacheKey, { tex: null, video: null });
  return [null, null];
}

/** glTF COLOR_0 → RGBA float на вершину; null если длина accessor не совпадает. */
function expandVertexColorRgba(
  value: ArrayLike<number>,
  nVerts: number,
  componentSizeHint?: number,
): Float32Array | null {
  if (nVerts <= 0) return null;
  const out = new Float32Array(nVerts * 4);
  if (value instanceof Uint8Array || value instanceof Uint8ClampedArray) {
    const per = value.length >= nVerts * 4 ? 4 : 3;
    if (value.length < nVerts * per) return null;
    for (let i = 0; i < nVerts; i++) {
      const o = i * per;
      out[i * 4] = value[o]! / 255;
      out[i * 4 + 1] = value[o + 1]! / 255;
      out[i * 4 + 2] = value[o + 2]! / 255;
      out[i * 4 + 3] = per >= 4 ? value[o + 3]! / 255 : 1;
    }
    return out;
  }
  const per =
    componentSizeHint === 3 || componentSizeHint === 4
      ? componentSizeHint
      : value.length >= nVerts * 4
        ? 4
        : 3;
  if (value.length < nVerts * per) return null;
  for (let i = 0; i < nVerts; i++) {
    const o = i * per;
    out[i * 4] = Number(value[o]);
    out[i * 4 + 1] = Number(value[o + 1]);
    out[i * 4 + 2] = Number(value[o + 2]);
    out[i * 4 + 3] = per >= 4 ? Number(value[o + 3]) : 1;
  }
  return out;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function readGhostNodeExtras(gltf: any, nodeIdx: number): {
  ghostKind: "character" | "picture" | null;
  ghostBillboardYawOffsetRad: number;
} {
  const node = gltf.nodes?.[nodeIdx];
  const ex = node?.extras;
  let ghostKind: "character" | "picture" | null = null;
  let yawDeg = 0;
  if (ex && typeof ex === "object") {
    const o = ex as Record<string, unknown>;
    const raw = o.ghost_kind ?? o.ghost_mode;
    if (typeof raw === "string") {
      const s = raw.toLowerCase().trim();
      if (s === "character") ghostKind = "character";
      else if (s === "picture") ghostKind = "picture";
    }
    const yv = o.ghost_billboard_yaw_deg;
    if (typeof yv === "number" && Number.isFinite(yv)) yawDeg = yv;
    else if (typeof yv === "string" && yv.trim() !== "") {
      const n = Number(yv);
      if (Number.isFinite(n)) yawDeg = n;
    }
  }
  return {
    ghostKind,
    ghostBillboardYawOffsetRad: (yawDeg * Math.PI) / 180,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildSubMesh(
  gl: WebGL2RenderingContext,
  gltf: any,
  prim: GltfPrimitive,
  nodeIdx: number,
  nodeName: string,
  texCache: MaterialTextureCache,
  pendingDecodes: Promise<void>[],
): BuildSubMeshResult | null {
  const pos = prim.attributes.POSITION;
  if (!pos) {
    console.warn("[mesh] primitive missing POSITION, skip");
    return null;
  }
  const positions = pos.value;
  const norm = prim.attributes.NORMAL;
  const uv = prim.attributes.TEXCOORD_0;
  const joints = prim.attributes.JOINTS_0;
  const weights = prim.attributes.WEIGHTS_0;
  let indices = prim.indices;

  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i], y = positions[i + 1], z = positions[i + 2];
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
    if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
  }

  const bodyLayer = resolveBodyLayer(gltf, prim, nodeName);
  let baseColorFactor = readBaseColorFactor(gltf, prim);
  let didRefineInner = false;
  let positionsArr = positions;
  let normData = norm ? norm.value : (() => {
    const f = new Float32Array(positions.length);
    for (let i = 0; i < f.length; i += 3) f[i + 2] = 1;
    return f;
  })();
  let uvData = uv ? uv.value : new Float32Array(positions.length / 3 * 2);
  let jointsU16: Uint16Array | null = null;
  let weightsArr: Float32Array | null = null;
  if (joints && weights) {
    jointsU16 = joints.value instanceof Uint16Array
      ? joints.value
      : Uint16Array.from(joints.value);
    weightsArr = weights.value;
    if (bodyLayer === "inner") {
      didRefineInner = true;
      const refined = refineInnerSkinMeshForBodyVox(
        positionsArr,
        normData,
        uvData,
        jointsU16,
        weightsArr,
        indices ? indices.value : (() => {
          const seq = new Uint32Array(positionsArr.length / 3);
          for (let i = 0; i < seq.length; i++) seq[i] = i;
          return seq;
        })(),
      );
      positionsArr = refined.positions;
      normData = refined.normals;
      uvData = refined.uvs;
      jointsU16 = refined.joints;
      weightsArr = refined.weights;
      indices = { value: refined.indices };
    }
  }

  const vertCount = positionsArr.length / 3;
  let colorRgba: Float32Array | null = null;
  if (!didRefineInner) {
    const c0 = prim.attributes.COLOR_0;
    if (c0?.value) {
      colorRgba = expandVertexColorRgba(c0.value, vertCount, c0.size);
    }
  }

  const vao = gl.createVertexArray();
  if (!vao) throw new Error("createVertexArray failed");
  gl.bindVertexArray(vao);

  // POSITION
  const posBuf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
  gl.bufferData(gl.ARRAY_BUFFER, positionsArr, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(ATTR_POS);
  gl.vertexAttribPointer(ATTR_POS, 3, gl.FLOAT, false, 0, 0);

  // NORMAL (или fake (0,0,1))
  const normBuf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, normBuf);
  gl.bufferData(gl.ARRAY_BUFFER, normData, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(ATTR_NORMAL);
  gl.vertexAttribPointer(ATTR_NORMAL, 3, gl.FLOAT, false, 0, 0);

  // UV (или (0,0))
  const uvBuf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, uvBuf);
  gl.bufferData(gl.ARRAY_BUFFER, uvData, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(ATTR_UV);
  gl.vertexAttribPointer(ATTR_UV, 2, gl.FLOAT, false, 0, 0);

  if (colorRgba) {
    const colorBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, colorBuf);
    gl.bufferData(gl.ARRAY_BUFFER, colorRgba, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(ATTR_COLOR);
    gl.vertexAttribPointer(ATTR_COLOR, 4, gl.FLOAT, false, 0, 0);
  } else {
    gl.disableVertexAttribArray(ATTR_COLOR);
  }

  // JOINTS_0 + WEIGHTS_0 (если есть)
  const hasSkin = !!(joints && weights);
  if (hasSkin) {
    // JOINTS — нормализуем к Uint16Array (uvec4 input в шейдере читает unsigned int)
    if (!jointsU16 || !weightsArr) {
      throw new Error("buildSubMesh: inner refine left joints/weights null");
    }
    const jointBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, jointBuf);
    gl.bufferData(gl.ARRAY_BUFFER, jointsU16!, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(ATTR_JOINTS);
    // I-pointer для integer atrribute (uvec4 в шейдере)
    gl.vertexAttribIPointer(ATTR_JOINTS, 4, gl.UNSIGNED_SHORT, 0, 0);

    const weightBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, weightBuf);
    gl.bufferData(gl.ARRAY_BUFFER, weightsArr!, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(ATTR_WEIGHTS);
    gl.vertexAttribPointer(ATTR_WEIGHTS, 4, gl.FLOAT, false, 0, 0);
  } else {
    // Default: joint=0, weight=(1,0,0,0) — тот же шейдер сработает,
    // но т.к. инстанс этого primitive'а будет рендериться с uUseSkin=0,
    // эти атрибуты не читаются.
    const fakeJ = new Uint16Array(positionsArr.length / 3 * 4);
    const fakeW = new Float32Array(positionsArr.length / 3 * 4);
    for (let i = 0; i < fakeW.length; i += 4) fakeW[i] = 1;
    const jBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, jBuf);
    gl.bufferData(gl.ARRAY_BUFFER, fakeJ, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(ATTR_JOINTS);
    gl.vertexAttribIPointer(ATTR_JOINTS, 4, gl.UNSIGNED_SHORT, 0, 0);
    const wBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, wBuf);
    gl.bufferData(gl.ARRAY_BUFFER, fakeW, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(ATTR_WEIGHTS);
    gl.vertexAttribPointer(ATTR_WEIGHTS, 4, gl.FLOAT, false, 0, 0);
  }

  // INDICES
  let indexCount: number;
  let indexType: GLenum;
  if (indices) {
    const ibo = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices.value, gl.STATIC_DRAW);
    indexCount = indices.value.length;
    indexType = indices.value instanceof Uint32Array ? gl.UNSIGNED_INT : gl.UNSIGNED_SHORT;
  } else {
    const seq = new Uint32Array(positionsArr.length / 3);
    for (let i = 0; i < seq.length; i++) seq[i] = i;
    const ibo = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, seq, gl.STATIC_DRAW);
    indexCount = seq.length;
    indexType = gl.UNSIGNED_INT;
  }

  gl.bindVertexArray(null);

  // ─── PBR-текстуры и альфа (glTF core; displacement в движке не вершинный). ───
  const mat = getPrimitiveMaterial(gltf, prim);
  let [diffuseTex, diffuseVideo] = loadTextureFromMaterialInfoCached(
    gl, gltf, mat?.pbrMetallicRoughness?.baseColorTexture,
    "baseColor", MESH_COLOR_SRGB, texCache.diffuse, pendingDecodes,
  );
  let normalTex: WebGLTexture | null = null;
  let normalScale = 1;
  let metallicRoughnessTex: WebGLTexture | null = null;
  let metallicFactor = 1;
  let roughnessFactor = 1;
  let alphaMode = 0;
  let alphaCutoff = 0.5;
  let doubleSided = false;
  if (mat) {
    const nt = mat.normalTexture;
    if (nt) {
      normalScale = typeof nt.scale === "number" && Number.isFinite(nt.scale) ? nt.scale : 1;
      normalTex = loadTextureFromMaterialInfoCached(
        gl, gltf, nt, "normal", false, texCache.normal, pendingDecodes,
      )[0];
    }
    const pbr = mat.pbrMetallicRoughness;
    if (pbr?.metallicRoughnessTexture) {
      metallicRoughnessTex = loadTextureFromMaterialInfoCached(
        gl, gltf, pbr.metallicRoughnessTexture,
        "metallicRoughness", false, texCache.metallicRoughness, pendingDecodes,
      )[0];
    }
    if (typeof pbr?.metallicFactor === "number" && Number.isFinite(pbr.metallicFactor)) {
      metallicFactor = pbr.metallicFactor;
    }
    if (typeof pbr?.roughnessFactor === "number" && Number.isFinite(pbr.roughnessFactor)) {
      roughnessFactor = pbr.roughnessFactor;
    }
    const am = String(mat.alphaMode ?? "OPAQUE").toUpperCase();
    if (am === "MASK") alphaMode = 1;
    else if (am === "BLEND") alphaMode = 2;
    if (typeof mat.alphaCutoff === "number" && Number.isFinite(mat.alphaCutoff)) {
      alphaCutoff = mat.alphaCutoff;
    }
    doubleSided = mat.doubleSided === true;
    const nn = nodeName.toLowerCase();
    if (!doubleSided && nn.includes("door")) doubleSided = true;
  }
  if (!doubleSided) {
    const nn = nodeName.toLowerCase();
    if (nn.includes("door")) doubleSided = true;
  }

  // ─── Fallback-инжект видео (см. fallbackVideoUriForNode выше). Срабатывает,
  // только если из glb видео НЕ пришло: инжект build_level всегда в приоритете.
  if (!diffuseVideo) {
    const nodeExtras =
      ((gltf.nodes?.[nodeIdx] as { extras?: Record<string, unknown> } | undefined)?.extras) ?? null;
    const fbUri = fallbackVideoUriForNode(nodeName, nodeExtras);
    if (fbUri) {
      // Дедуп на уровне <video>+текстуры: обе шторы играют ОДИН элемент.
      const cacheKey = `nodevid:${fbUri}`;
      let hit = texCache.diffuse.get(cacheKey);
      if (!hit) {
        const origin =
          typeof window !== "undefined" && window.location?.origin ? window.location.origin : "";
        const absUri = fbUri.startsWith("/") ? `${origin}${fbUri}` : fbUri;
        const spec = buildGhostVideoSpec(absUri, nodeExtras);
        const vid = createGhostVideoElement(spec);
        vid.addEventListener("error", () => {
          console.warn(
            `[mesh] video-fallback: '${nodeName}' ← ${fbUri} не загрузилось (файла нет?)`,
            vid.error?.code, vid.error?.message,
          );
        }, { once: true });
        const tex = uploadTexture2D(gl, vid, {
          label: `nodeVideo:${nodeName}`, srgb: MESH_COLOR_SRGB, video: true,
        });
        hit = { tex, video: tex ? vid : null };
        texCache.diffuse.set(cacheKey, hit);
        console.log(`[mesh] video-fallback: node '${nodeName}' ← ${fbUri}`);
      }
      if (hit.tex && hit.video) {
        diffuseTex = hit.tex;
        diffuseVideo = hit.video;
        // Видео-оверлей рендерится unlit-BLEND'ом (mesh.frag, ветка
        // uDiffuseVideoBillboard): материал из Blender тут обычно пустой или
        // OPAQUE — приводим к тому же виду, что даёт инжект build_level.
        alphaMode = 2;
        doubleSided = true;
        baseColorFactor = [1, 1, 1, 1];
      }
    }
  }

  // CPU-копии для пересчёта bbox в реальной позе.
  const cpuJoints = hasSkin && jointsU16 ? jointsU16 : null;
  const cpuWeights = hasSkin && weightsArr ? weightsArr : null;

  const ghostEx = readGhostNodeExtras(gltf, nodeIdx);

  return {
    subMesh: {
      vao,
      indexCount,
      indexType,
      diffuseTex,
      diffuseVideo,
      normalTex,
      normalScale,
      metallicRoughnessTex,
      metallicFactor,
      roughnessFactor,
      alphaMode,
      alphaCutoff,
      baseColorFactor,
      hasSkin,
      nodeIdx,
      cpuVerts: { positions: positionsArr, joints: cpuJoints, weights: cpuWeights },
      bboxMin: [minX, minY, minZ],
      bboxMax: [maxX, maxY, maxZ],
      bodyLayer,
      doubleSided,
      ghostKind: ghostEx.ghostKind,
      ghostBillboardYawOffsetRad: ghostEx.ghostBillboardYawOffsetRad,
    },
    bboxMin: [minX, minY, minZ],
    bboxMax: [maxX, maxY, maxZ],
  };
}

